/**
 * 기본 상세 테이블 이벤트 핸들러 훅
 * 모든 상세 테이블에서 공통으로 사용하는 기본 기능만 제공
 */

import { useCallback, useMemo } from "react";
import { CreateCellClickHandler, CustomCellClickAction } from "src/utils/grid/GridHandlerContent";
import { 
  DetailTableCore, 
  BasicDetailTableEventsProps, 
  DetailTableEventHandlers 
} from "src/types/detail-table-new";

export function useDetailTableEvents<T extends DetailTableCore>({
  detailList,
  editableColumns = [],
  keyDownConfig,
}: BasicDetailTableEventsProps<T>): DetailTableEventHandlers<T> {

  /**
   * 셀 클릭 핸들러 생성
   */
  const cellClickActions: CustomCellClickAction<T>[] = useMemo(() => [
    {
      validate: (field) => editableColumns.includes(field),
      action: (e) => {
        e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
      }
    }
  ], [editableColumns]);

  const handleCellClick = CreateCellClickHandler(detailList, 'valueIndex', cellClickActions);

  /**
   * 키다운 핸들러 (엔터/탭 키 네비게이션)
   */
  const handleCellKeyDown = useCallback((event: any) => {
    // Ctrl + 방향키 좌/우: 에디터 커서 이동 대신 편집 포커스 이동
    if ((event.event.key === 'ArrowLeft' || event.event.key === 'ArrowRight') && event.event.ctrlKey) {
      if (!keyDownConfig?.enableArrowNavigation) return;
      
      const { api, column, rowIndex } = event;
      event.event.preventDefault();
      event.api.stopEditing();

      const allColumns = api.getAllDisplayedColumns() as any[];
      const colId = column.getColId();

      const buildEditableColumnsForRow = (targetRowIndex: number) => {
        const rowNode = api.getDisplayedRowAtIndex(targetRowIndex);
        const isColumnEditable = (col: any) => {
          const colDef = col.getColDef ? col.getColDef() : col.colDef;
          if (!colDef) return false;
          if (typeof colDef.editable === 'function') {
            try {
              return !!colDef.editable({
                data: rowNode?.data,
                node: rowNode,
                colDef,
                column: col,
                api,
                rowIndex: targetRowIndex,
              });
            } catch {
              return false;
            }
          }
          return !!colDef.editable;
        };
        return (allColumns as any[])
          .filter((c: any) => editableColumns.includes(c.getColId()))
          .filter((c: any) => isColumnEditable(c));
      };

      const currentEditableColumns = buildEditableColumnsForRow(rowIndex);
      if (currentEditableColumns.length === 0) return;

      const currentEditableIndex = currentEditableColumns.findIndex((c: any) => c.getColId() === colId);

      if (event.event.key === 'ArrowRight') {
        if (currentEditableIndex > -1 && currentEditableIndex < currentEditableColumns.length - 1) {
          const nextCol = currentEditableColumns[currentEditableIndex + 1];
          api.setFocusedCell(rowIndex, nextCol);
          api.startEditingCell({ rowIndex, colKey: nextCol.getColId() });
          return;
        }
        // 행 끝 → 다음 행 첫 편집 가능 컬럼
        const nextRowIndex = rowIndex + 1;
        if (nextRowIndex < api.getDisplayedRowCount()) {
          const nextEditableColumns = buildEditableColumnsForRow(nextRowIndex);
          if (nextEditableColumns.length > 0) {
            const firstCol = nextEditableColumns[0];
            api.setFocusedCell(nextRowIndex, firstCol);
            api.startEditingCell({ rowIndex: nextRowIndex, colKey: firstCol.getColId() });
          }
        }
        return;
      }

      if (event.event.key === 'ArrowLeft') {
        if (currentEditableIndex > 0) {
          const prevCol = currentEditableColumns[currentEditableIndex - 1];
          api.setFocusedCell(rowIndex, prevCol);
          api.startEditingCell({ rowIndex, colKey: prevCol.getColId() });
          return;
        }
        // 행 시작 → 이전 행 마지막 편집 가능 컬럼
        const prevRowIndex = rowIndex - 1;
        if (prevRowIndex >= 0) {
          const prevEditableColumns = buildEditableColumnsForRow(prevRowIndex);
          if (prevEditableColumns.length > 0) {
            const lastCol = prevEditableColumns[prevEditableColumns.length - 1];
            api.setFocusedCell(prevRowIndex, lastCol);
            api.startEditingCell({ rowIndex: prevRowIndex, colKey: lastCol.getColId() });
          }
        }
        return;
      }
    }

    // Ctrl + 방향키 위/아래: 숫자 증감 방지하고 편집 포커스만 이동
    if ((event.event.key === 'ArrowUp' || event.event.key === 'ArrowDown') && event.event.ctrlKey) {
      if (!keyDownConfig?.enableArrowNavigation) return;
      
      const { api, column, rowIndex } = event;
      event.event.preventDefault();
      event.api.stopEditing();

      const targetRowIndex = event.event.key === 'ArrowUp' ? rowIndex - 1 : rowIndex + 1;
      if (targetRowIndex < 0 || targetRowIndex >= api.getDisplayedRowCount()) return;

      const allColumns = api.getAllDisplayedColumns() as any[];
      const colId = column.getColId();

      const rowNode = api.getDisplayedRowAtIndex(targetRowIndex);
      const isColumnEditable = (col: any) => {
        const colDef = col.getColDef ? col.getColDef() : col.colDef;
        if (!colDef) return false;
        if (typeof colDef.editable === 'function') {
          try {
            return !!colDef.editable({
              data: rowNode?.data,
              node: rowNode,
              colDef,
              column: col,
              api,
              rowIndex: targetRowIndex,
            });
          } catch {
            return false;
          }
        }
        return !!colDef.editable;
      };

      const editableDisplayedColumns = (allColumns as any[])
        .filter((col: any) => editableColumns.includes(col.getColId()))
        .filter((col: any) => isColumnEditable(col));

      const sameColEditable = editableDisplayedColumns.find((col: any) => col.getColId() === colId);
      const nextColKey = (sameColEditable || editableDisplayedColumns[0])?.getColId();
      if (!nextColKey) return;

      api.setFocusedCell(targetRowIndex, nextColKey);
      api.startEditingCell({ rowIndex: targetRowIndex, colKey: nextColKey });
      return;
    }

    if (event.event.key === 'Enter' || event.event.key === 'Tab') {
      const { api, column, rowIndex } = event;
      event.api.stopEditing();

      setTimeout(() => {
        const allColumns = api.getAllDisplayedColumns() as any[];
        const colId = column.getColId();

        // 코드 검색 필드에서 엔터/탭 시 수량 셀로 이동
        if (keyDownConfig?.qtyField && colId === 'itemCd') {
          const qtyCol = allColumns.find((col: any) => col.getColId() === keyDownConfig.qtyField);
          if (qtyCol) {
            api.startEditingCell({
              rowIndex,
              colKey: qtyCol.getColId()
            });
            return;
          }
        }

        // 편집 가능한 컬럼 기준 네비게이션
        const rowNode = api.getDisplayedRowAtIndex(rowIndex);
        const isColumnEditable = (col: any) => {
          const colDef = col.getColDef ? col.getColDef() : col.colDef;
          if (!colDef) return false;
          if (typeof colDef.editable === 'function') {
            try {
              return !!colDef.editable({
                data: rowNode?.data,
                node: rowNode,
                colDef,
                column: col,
                api,
                rowIndex,
              });
            } catch {
              return false;
            }
          }
          return !!colDef.editable;
        };
        const editableDisplayedColumns = (allColumns as any[])
          .filter((col: any) => editableColumns.includes(col.getColId()))
          .filter((col: any) => isColumnEditable(col));
        const currentEditableIndex = editableDisplayedColumns.findIndex((col: any) => col.getColId() === colId);

        if (currentEditableIndex !== -1) {
          const nextEditableIndex = currentEditableIndex + 1;
          if (nextEditableIndex < editableDisplayedColumns.length) {
            api.startEditingCell({
              rowIndex,
              colKey: editableDisplayedColumns[nextEditableIndex].getColId()
            });
            return;
          }

          // 마지막 편집 가능 컬럼이면 다음 행 첫 편집 가능 컬럼로 이동
          const nextRowIndex = rowIndex + 1;
          if (api.getDisplayedRowCount() > nextRowIndex && editableDisplayedColumns.length > 0) {
            const firstEditableColId = editableDisplayedColumns[0].getColId();
            api.setFocusedCell(nextRowIndex, firstEditableColId);
            api.startEditingCell({ rowIndex: nextRowIndex, colKey: firstEditableColId });
            return;
          }
        }

        // 현재 컬럼이 편집 가능 목록에 없으면 기존 로직으로 폴백
        const colIndex = allColumns.findIndex((col: any) => col.getColId() === colId);
        let nextColIndex = colIndex + 1;
        let nextRowIndex = rowIndex;

        if (nextColIndex >= allColumns.length) {
          nextColIndex = 0;
          nextRowIndex = rowIndex + 1;
        }

        if (api.getDisplayedRowCount() > nextRowIndex) {
          api.startEditingCell({
            rowIndex: nextRowIndex,
            colKey: allColumns[nextColIndex].getColId()
          });
        }
      }, 0);
    }
  }, [editableColumns, keyDownConfig]);

  return {
    handleCellClick,
    handleCellKeyDown,
  };
}
